import { Point, Rectangle } from '@shared/commons/Geometry';
import { SolidBrush } from '@shared/commons/Graphics';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { TerceraChartMouseEventArgs } from './Utils/TerceraChartMouseEventArgs';
import { Layer } from './Utils/Layer';
import { Enum } from '@shared/utils/Enum';
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Cursors } from '@shared/commons/Cursors';
import { MouseButtons } from '@front/controls/UtilsClasses/ControlsUtils';
import { TerceraChartMouseState } from './Utils/TerceraChartMouseState';
import { TerceraChartPriceScaleLayoutInfo } from './Utils/TerceraChartPriceScaleLayoutInfo';
import { TerceraChartAdvancedParams } from './Utils/TerceraChartAdvancedParams';
import { TerceraChartTouchEventArgs } from './Utils/TerceraChartTouchEventArgs';
import { LayersEnum } from './Utils/LayersEnum';
export class TerceraChartBase {
    constructor(ctx, terceraChartPanelContext) {
        this.width = 0;
        this.height = 0;
        this.w = 0;
        this.h = 0;
        this.OnUpdateTooltip = new CustomEvent();
        this.onHasCorrectDataChanged = new CustomEvent();
        this.HoverWindow = null;
        this.windowsContainer = null;
        this.mainWindow = null;
        this.needRedraw = true;
        this.layersCashedData = {};
        this.lastMouseDownState = null;
        this.lastMouseMoveArgs = null;
        this.MouseIn = false;
        this.MouseState = null;
        this.lastMouseDownI1 = null;
        this.lastMouseDownMaxX = null;
        this.lastMouseDownMinX = null;
        this.lastMouseDownMaxY = null;
        this.lastMouseDownMinY = null;
        this.MovingAlloved = true;
        this.snapToCandle = false;
        this.NoDataMessage = 'No data';
        this.LoadingNow = false;
        this._forceUpdateInitiators = [];
        this._rightBarIndexIncludingEmptyBars = 0;
        this.layers = {};
        this.isProcessedDown = false;
        this.context = ctx;
        this.terceraChartPanelContext = terceraChartPanelContext;
        this.initDrawingsLayers();
    }
    get rightBarIndexIncludingEmptyBars() {
        return this._rightBarIndexIncludingEmptyBars;
    }
    set rightBarIndexIncludingEmptyBars(value) {
        this.mainWindow.i1 = value;
        this._rightBarIndexIncludingEmptyBars = value;
    }
    Initialize() {
        this.windowsContainer = this.CreateWindowsContainer();
        this.mainWindow = this.CreateMainWindow();
        this.mainWindow.IsMainWindow = true;
        this.windowsContainer.Windows.push(this.mainWindow);
        this.InitializeRenderers();
        this.layoutMainWindow();
    }
    getType() {
        return 'TerceraChartBase';
    }
    // #region ThemeChanged/Localize
    ThemeChanged() {
        var _a, _b;
        this.TerceraChartNoDataBrushColor = new SolidBrush(ThemeManager.colors.chartNoDataForeground);
        this.TerceraChartFont = ThemeManager.Fonts.Font_10F_regular.copy();
        this.windowsContainer.themeChanged();
        (_a = this.yScaleRendererSettings) === null || _a === void 0 ? void 0 : _a.ThemeChanged();
        (_b = this.xScaleRendererSettings) === null || _b === void 0 ? void 0 : _b.ThemeChanged();
        this.IsDirty();
    }
    Localize() {
        const windwsContainer = this.windowsContainer;
        if (isNullOrUndefined(windwsContainer)) {
            return;
        }
        windwsContainer.Localize();
        this.IsDirty();
    }
    // #endregion
    // #region Properties/Callback
    Properties() {
        let properties = [];
        const len = this.windowsContainer.Renderers.length;
        for (let i = 0; i < len; i++) {
            properties = properties.concat(this.windowsContainer.Renderers[i].Properties());
        }
        const windowsLength = this.windowsContainer.Windows.length;
        for (let windowIndex = 0; windowIndex < windowsLength; windowIndex++) {
            const window = this.windowsContainer.Windows[windowIndex];
            const renderersLength = window.Renderers.length;
            for (let rendererIndex = 0; rendererIndex < renderersLength; rendererIndex++) {
                properties = properties.concat(window.Renderers[rendererIndex].Properties());
            }
        }
        return properties;
    }
    callBack(properties) {
        const len = this.windowsContainer.Renderers.length;
        for (let i = 0; i < len; i++) {
            this.windowsContainer.Renderers[i].callBack(properties);
        }
        const windowsLength = this.windowsContainer.Windows.length;
        for (let windowIndex = 0; windowIndex < windowsLength; windowIndex++) {
            const window = this.windowsContainer.Windows[windowIndex];
            const renderersLength = window.Renderers.length;
            for (let rendererIndex = 0; rendererIndex < renderersLength; rendererIndex++) {
                window.Renderers[rendererIndex].callBack(properties);
            }
        }
    }
    // #endregion
    // #region EventsProcessors
    ImproveArgs(ev) {
        const X = ev.offsetX;
        const Y = ev.offsetY;
        const HoverWindow = this.SetWindowCursorPosition(X, Y);
        // Стараемся не перерисовывать чарт на каждое движение мыши
        const result = new TerceraChartMouseEventArgs(HoverWindow, ev);
        return result;
    }
    ProcessMouseDown(e) {
        this.lastMouseDownState = e.Location;
        const isProcessed = (this.isProcessedDown = this.windowsContainer.ProcessMouseDown(e));
        if (!isProcessed && this.BarsCount() > 0) {
            if (e.Button !== MouseButtons.Left) {
                return false;
            }
            // Тягание чарта?
            const activeWindow = this.GetActiveWindowFromEvent(e);
            if (isNullOrUndefined(activeWindow) || !activeWindow.ClientRectangle.Contains(e.Location.X, e.Location.Y)) {
                return false;
            }
            this.lastMouseDownI1 = this.rightBarIndexIncludingEmptyBars; // ! from main
            this.lastMouseDownMaxX = activeWindow.FmaxFloatX;
            this.lastMouseDownMinX = activeWindow.FminFloatX;
            this.lastMouseDownMaxY = activeWindow.FmaxFloatY;
            this.lastMouseDownMinY = activeWindow.FminFloatY;
            this.MouseState = TerceraChartMouseState.Moving;
            return true;
        }
    }
    ProcessMouseUp(e) {
        const mouseProcessed = this.windowsContainer.ProcessMouseUp(e);
        if (mouseProcessed) {
            this.UpdateCursor(e);
            this.UpdateTooltip(e);
        }
        this.MouseState = TerceraChartMouseState.None;
        this.IsDirty();
        if (!mouseProcessed) {
            if (e.Button === MouseButtons.Right) {
                const contextMenuItems = this.GetContextMenuItems(e, this);
                if (isValidArray(contextMenuItems)) {
                    showContextMenu({
                        x: e.ClientLocation.X,
                        y: e.ClientLocation.Y,
                        items: contextMenuItems,
                        minWidth: 300
                    });
                }
            }
        }
        this.isProcessedDown = false;
        return mouseProcessed;
    }
    ProcessMouseClick(e) {
        return this.windowsContainer.ProcessMouseClick(e);
    }
    ProcessMouseDoubleClick(e) {
        if (!this.HasCorrectData()) {
            return false;
        }
        return this.windowsContainer.ProcessMouseDoubleClick(e);
    }
    ProcessMouseWheel(e) {
        if (!this.HasCorrectData()) {
            return false;
        }
        let res = false;
        res = this.windowsContainer.ProcessMouseWheel(e);
        if (res) {
            this.IsDirty();
        }
        return res;
    }
    ProcessMultiTouches(e) {
        let res = false;
        res = this.windowsContainer.ProcessMultiTouches(e);
        if (res) {
            this.IsDirty();
        }
        return res;
    }
    ProcessMouseEnter(e) {
        this.lastMouseMoveArgs = e;
        if (!this.HasCorrectData()) {
            return;
        }
        try {
            this.windowsContainer.ProcessMouseEnter(e);
            this.MouseIn = true;
            this.UpdateCursor(e);
            this.IsDirty();
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
        }
    }
    ProcessMouseMove(e) {
        let mouseMoveProcessed = false;
        this.lastMouseMoveArgs = e;
        if (!this.HasCorrectData()) {
            return false;
        }
        // Идёт тягание чарта
        if (this.MouseState === TerceraChartMouseState.Moving && this.MovingAlloved) {
            const xDelta = e.Location.X - this.lastMouseDownState.X;
            if (xDelta !== 0) {
                this.moveScreenByX(this.calulateNewI1FromDelta(xDelta));
            }
            const yDelta = e.Location.Y - this.lastMouseDownState.Y;
            const activeWindow = this.GetActiveWindow();
            if (yDelta !== 0 && !isNullOrUndefined(activeWindow) && !activeWindow.AutoScale) {
                this.moveScreenByY(activeWindow, yDelta);
            }
            this.UpdateCursor(e);
            mouseMoveProcessed = true;
            e.NeedRedraw = true;
        }
        else {
            try {
                mouseMoveProcessed = this.windowsContainer.ProcessMouseMove(e);
            }
            finally {
                this.UpdateCursor(e);
                this.UpdateTooltip(e);
            }
            if (!mouseMoveProcessed) {
                const len = this.mainWindow.Renderers.length;
                let res;
                for (let i = 0; i < len; i++) {
                    res = this.mainWindow.Renderers[i].ProcessMouseMove(e);
                }
            }
        }
        if (e.NeedRedraw === true) {
            this.IsDirty();
        }
        else if (!e.NeedRedraw) {
        }
        else if (Array.isArray(e.NeedRedraw)) {
            const len = e.NeedRedraw.length;
            for (let i = 0; i < len; i++) {
                this.IsDirty(e.NeedRedraw[i]);
            }
        }
        else {
            this.IsDirty(e.NeedRedraw);
        }
        return mouseMoveProcessed;
    }
    ProcessMouseLeave(e) {
        if (!this.HasCorrectData()) {
            return;
        }
        try {
            this.windowsContainer.ProcessMouseLeave(e);
            this.HoverWindow = null;
            this.CursorPosition = Point.Empty();
            this.HoverWindowYValue = 0;
            this.MouseIn = false;
            this.IsDirty();
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
        }
        this.UpdateTooltip(null);
    }
    OnKeyDown() { }
    OnResize(width, height) {
        this.w = width;
        this.h = height;
        const resetSize = 10;
        if (width <= resetSize || height <= resetSize) {
            return;
        }
        this.resizeLayers(width, height);
        this.windowsContainer.Rectangle.Width = width;
        this.windowsContainer.Rectangle.Height = height;
        this.windowsContainer.LayoutWindows();
        this.CheckBarsCountOnScreen();
        this.IsDirty();
    }
    // #region Touch events
    OnTouchStart(event, offsetX, offsetY) {
        return this.createChartTouchEvent(event, offsetX, offsetY);
    }
    OnTouchMove(event, offsetX, offsetY) {
        return this.createChartTouchEvent(event, offsetX, offsetY);
    }
    OnTouchEnd(event, offsetX, offsetY) {
        return this.createChartTouchEvent(event, offsetX, offsetY);
    }
    createChartTouchEvent(event, offsetX, offsetY) {
        const touches = this.getTouches(event);
        const isSingle = touches.length === 1;
        const touchClientLocation = this.getTouchClientLocation(touches[0], offsetX, offsetY);
        const touchPageLocation = this.getTouchPageLocation(touches[0], offsetX, offsetY);
        const window = this.SetWindowCursorPosition(touchClientLocation.X, touchClientLocation.Y);
        const touchEvent = new TerceraChartTouchEventArgs(window, touchClientLocation, touchPageLocation, isSingle);
        touchEvent.TouchList.push(touchEvent);
        for (let i = 1; i < touches.length; i++) {
            const additionalTouchClientLocation = this.getTouchClientLocation(touches[i], offsetX, offsetY);
            const additionalTouchPageLocation = this.getTouchPageLocation(touches[i], offsetX, offsetY);
            const additonalTouchWindow = this.SetWindowCursorPosition(additionalTouchClientLocation.X, additionalTouchClientLocation.Y);
            touchEvent.TouchList.push(new TerceraChartTouchEventArgs(additonalTouchWindow, additionalTouchClientLocation, additionalTouchPageLocation, isSingle));
        }
        touchEvent.pinchYScale = this.checkIfPinchOnYScale(touchEvent.TouchList, this.GetYScaleRectangles());
        return touchEvent;
    }
    checkIfPinchOnYScale(touchList, rects) {
        return (touchList.length === 2 && touchList.some((point) => rects.some((rect) => rect.Contains(point.X, point.Y))));
    }
    getTouches(event) {
        return event.targetTouches.length > 1 ? event.targetTouches : [event.changedTouches[0]];
    }
    getTouchClientLocation(touch, offsetX, offsetY) {
        const x = Math.max(0, touch.clientX - offsetX);
        const y = Math.max(0, touch.clientY - offsetY);
        return new Point(x, y);
    }
    getTouchPageLocation(touch, offsetX, offsetY) {
        const x = touch.pageX - offsetX;
        const y = touch.pageY - offsetY;
        return new Point(x, y);
    }
    // #endregion
    CheckBarsCountOnScreen() { }
    GetYScaleRectangles() {
        const res = [];
        // Process main chart windows
        this.windowsContainer.Windows.forEach((window) => {
            if (!isNullOrUndefined(window.leftYScaleRenderer) && window.leftYScaleRenderer.Rectangle.Width > 0) {
                res.push(window.leftYScaleRenderer.Rectangle);
            }
            if (!isNullOrUndefined(window.rightYScaleRenderer) && window.rightYScaleRenderer.Rectangle.Width > 0) {
                res.push(window.rightYScaleRenderer.Rectangle);
            }
        });
        return res;
    }
    // #endregion
    // #region Layers
    initDrawingsLayers() {
        if (typeof window === 'undefined') {
            return;
        }
        const keys = Enum.TakeValueFromEnum(LayersEnum);
        const len = keys.length;
        const l = {};
        for (let i = 0; i < len; i++) {
            l[keys[i]] = new Layer(document.createElement('canvas'));
        }
        this.layers = l;
        this.layersCashedData.keys = Object.keys(this.layers);
        this.layersCashedData.layersLength = this.layersCashedData.keys.length;
    }
    clearLayers() {
        const len = this.layersCashedData.layersLength;
        for (let i = 0; i < len; i++) {
            const lay = this.layers[this.layersCashedData.keys[i]];
            if (lay.isDirty) {
                lay.context.clearRect(0, 0, this.w, this.h);
            }
        }
    }
    resizeLayers(w, h) {
        const devicePixelRatio = (window === null || window === void 0 ? void 0 : window.devicePixelRatio) || 1;
        const len = this.layersCashedData.layersLength;
        for (let i = 0; i < len; i++) {
            const lay = this.layers[this.layersCashedData.keys[i]];
            lay.canvas.width = w * devicePixelRatio;
            lay.canvas.height = h * devicePixelRatio;
            lay.context.scale(devicePixelRatio, devicePixelRatio);
        }
    }
    needRedrawAllLayers() {
        const len = this.layersCashedData.layersLength;
        for (let i = 0; i < len; i++) {
            this.layers[this.layersCashedData.keys[i]].isDirty = true;
        }
    }
    drawLayersData() {
        const len = this.layersCashedData.layersLength;
        for (let i = 0; i < len; i++) {
            const lay = this.layers[this.layersCashedData.keys[i]];
            this.context.drawImage(lay.canvas, 0, 0, this.w, this.h);
            lay.isDirty = false;
        }
    }
    IsDirty(result = true) {
        if (result === true) {
            this.needRedrawAllLayers();
        }
        else {
            this.layers[result].isDirty = true;
        }
        this.needRedraw = true;
    }
    // #endregion
    // #region Tooltip
    GetTooltip(e) {
        const len = this.mainWindow.Renderers.length;
        for (let i = 0; i < len; i++) {
            const renderer = this.mainWindow.Renderers[i];
            const newTooltip = renderer.GetTooltip(e);
            if (newTooltip != null) {
                return newTooltip;
            }
        }
        const newTooltip = this.windowsContainer.GetTooltip(e);
        if (newTooltip != null) {
            return newTooltip;
        }
        return null;
    }
    UpdateTooltip(e) {
        if (isNullOrUndefined(e)) {
            this.OnUpdateTooltip.Raise(null);
            return;
        }
        let newTooltip = null;
        try {
            newTooltip = this.GetTooltip(e);
        }
        finally {
            this.OnUpdateTooltip.Raise(newTooltip);
        }
    }
    // #endregion
    // #region Cursor
    GetCursor(e) {
        const len = this.mainWindow.Renderers.length;
        for (let i = 0; i < len; i++) {
            const renderer = this.mainWindow.Renderers[i];
            const newCursor = renderer.GetCursor(e);
            if (!isNullOrUndefined(newCursor)) {
                return newCursor;
            }
        }
        const newCursor = this.windowsContainer.GetCursor(e);
        return !isNullOrUndefined(newCursor) ? newCursor : null;
    }
    UpdateCursor(e = null) {
        var _a;
        const mouseEvent = e !== null && e !== void 0 ? e : this.lastMouseMoveArgs;
        if (isNullOrUndefined(mouseEvent)) {
            return;
        }
        let newCursor = null;
        try {
            newCursor = (_a = this.GetCursor(mouseEvent)) !== null && _a !== void 0 ? _a : Cursors.Default;
        }
        finally {
            if (this.Cursor !== newCursor) {
                this.IsDirty(LayersEnum.CrossHair);
                this.Cursor = newCursor;
                this.terceraChartPanelContext.applyCursor(newCursor);
            }
        }
    }
    GetActiveWindow() {
        return this.HoverWindow;
    }
    GetActiveWindowFromEvent(e) {
        var _a;
        return (_a = e.window) !== null && _a !== void 0 ? _a : this.mainWindow;
    }
    GetHoverWindow(point) {
        for (let i = 0; i < this.windowsContainer.Windows.length; i++) {
            if (this.windowsContainer.Windows[i].Rectangle.Contains(point.X, point.Y)) {
                return this.windowsContainer.Windows[i];
            }
        }
        return null;
    }
    GetWindowByPoint(point) {
        if (this.windowsContainer == null) {
            return null;
        }
        if (this.windowsContainer.Windows != null) {
            for (let i = 0; i < this.windowsContainer.Windows.length; i++) {
                const clientRect = this.windowsContainer.Windows[i].ClientRectangle;
                if (clientRect.Contains(point.X, point.Y)) {
                    return this.windowsContainer.Windows[i];
                }
            }
        }
        return null;
    }
    GetLastMouseDownWindow() {
        return this.GetWindowByPoint(this.lastMouseDownState);
    }
    SetWindowCursorPosition(X, Y) {
        this.CursorPosition = new Point(X, Y);
        const HoverWindow = this.GetHoverWindow(new Point(X, Y));
        if (HoverWindow != null) {
            this.HoverWindowXValue = HoverWindow.PointsConverter.GetDataX(X);
            this.HoverWindowYValue = HoverWindow.PointsConverter.GetDataY(Y);
        }
        else {
            this.HoverWindowYValue = 0;
            this.HoverWindowXValue = 0;
        }
        this.HoverWindow = HoverWindow;
        return HoverWindow;
    }
    LayoutWindowsContainers() {
        if (this.windowsContainer != null) {
            this.windowsContainer.Rectangle.Width = this.w;
            this.windowsContainer.Rectangle.Height = this.h;
        }
        this.windowsContainer.LayoutWindows();
        this.IsDirty();
    }
    moveScreenByY(activeWindow, yDelta) {
        const mouseDownWindow = this.GetLastMouseDownWindow();
        if (mouseDownWindow === activeWindow) {
            const delta = yDelta / (activeWindow.ClientRectangle.Height / (activeWindow.FmaxFloatY - activeWindow.FminFloatY));
            activeWindow.MoveY(this.lastMouseDownMinY + delta, this.lastMouseDownMaxY + delta);
        }
    }
    moveScreenByX(newI1) {
        if (this.isNeedUpdateRightIndex(newI1)) {
            this.rightBarIndexIncludingEmptyBars = newI1;
            return true;
        }
        else {
            return false;
        }
    }
    ToBegin() {
        const barsCount = this.BarsCount();
        if (barsCount === 0) {
            return;
        }
        const offset = this.getToBeginOffset();
        this.moveScreenByX(barsCount + offset - 1);
    }
    ToCustomPositionOnChart(newPosition) {
        const barsCount = this.BarsCount();
        if (barsCount === 0) {
            return;
        }
        this.moveScreenByX(newPosition);
    }
    BarsCount() {
        return 0;
    }
    HasCorrectData() {
        return this.BarsCount() > 0;
    }
    FindIntervalExactly(dataX) {
        return 0;
    }
    CalculateMinMax() {
        this.windowsContainer.CalculateMinMax();
    }
    getSnapToCandle() {
        return this.snapToCandle;
    }
    setSnapToCandle(value) {
        if (this.snapToCandle === value) {
            return;
        }
        this.snapToCandle = value;
        let i = 0;
        const len = this.mainWindow.Renderers.length;
        for (i; i < len; i++) {
            if (this.mainWindow.Renderers[i].ApplyNewSnapMode) {
                this.mainWindow.Renderers[i].ApplyNewSnapMode(this.snapToCandle);
            }
        }
    }
    XScaleRect() {
        return this.windowsContainer.xScaleRenderer.Rectangle;
    }
    ZoomToRectangle(r, startPoint) {
        const mainWindow = this.mainWindow;
        if (r.Width < mainWindow.XScale) {
            return;
        }
        const i1Time = mainWindow.PointsConverter.GetDataX(r.X + r.Width);
        const newI1Index = this.FindIntervalExactly(i1Time) - 1;
        const barsInRect = r.Width / mainWindow.XScale;
        const newxScale = mainWindow.ClientRectangle.Width / barsInRect;
        mainWindow.CheckAndSetXScale(Math.round(newxScale));
        this.moveScreenByX(Math.round(newI1Index));
        const clickedWindow = this.GetWindowByPoint(startPoint);
        if (clickedWindow) {
            const clickedWindowPC = clickedWindow.PointsConverter;
            const RY = r.Y;
            const RB = RY + r.Height;
            const windows = this.windowsContainer.Windows;
            const wLen = windows.length;
            for (let i = 0; i < wLen; i++) {
                const ww = windows[i];
                // Анализируем окно, в котором делали зуминг - устаналвиваем значения min/max
                if (ww === clickedWindow) {
                    clickedWindow.AutoScale = false;
                    clickedWindow.FmaxFloatY = clickedWindowPC.GetDataY(RY);
                    clickedWindow.FminFloatY = clickedWindowPC.GetDataY(RB);
                }
                // Для остальных окон цену не имеем права ставить, поэтом просто autofit'им
                else {
                    if (!ww.AutoScale) {
                        ww.AutoFit();
                    }
                }
            }
        }
        this.Draw();
    }
    calulateNewI1FromDelta(deltaX) {
        return this.lastMouseDownI1 - deltaX;
    }
    // #endregion
    // #region Context menu
    GetContextMenuItems(e, chart) {
        return this.HasCorrectData() ? this.windowsContainer.GetContextMenu(e, chart) : [];
    }
    // #endregion
    // #region Drawing
    GetAdvancedParamsForRenderer(gr) {
        const p = new TerceraChartAdvancedParams();
        p.Font = this.TerceraChartFont;
        p.CursorPosition = this.CursorPosition;
        p.HoverWindowXValue = this.HoverWindowXValue;
        p.HoverWindowYValue = this.HoverWindowYValue;
        p.TerceraChart = this;
        p.DrawPointers = [];
        p.LayoutInfo = this.GetTerceraChartPriceScaleLayoutInfo(gr);
        p.layers = this.layers;
        p.Tag = {};
        p.layerId = LayersEnum.Main;
        p.exitAfterFirst = null;
        p.isNeedConnectFirstAndLastPoints = true;
        return p;
    }
    GetTerceraChartPriceScaleLayoutInfo(gr) {
        const res = new TerceraChartPriceScaleLayoutInfo();
        let rightMaxWidth = 0;
        let leftMaxWidth = 0;
        for (let i = 0; i < this.windowsContainer.Windows.length; i++) {
            const rightYScaleRenderer = this.windowsContainer.Windows[i].rightYScaleRenderer;
            if (!isNullOrUndefined(rightYScaleRenderer)) {
                const curRightWidth = this.windowsContainer.Windows[i].rightYScaleRenderer.GetPreferredWidth(gr);
                if (rightMaxWidth < curRightWidth) {
                    rightMaxWidth = curRightWidth;
                }
            }
            const leftYScaleRenderer = this.windowsContainer.Windows[i].leftYScaleRenderer;
            if (!isNullOrUndefined(leftYScaleRenderer)) {
                const curLeftWidth = this.windowsContainer.Windows[i].leftYScaleRenderer.GetPreferredWidth(gr);
                if (leftMaxWidth < curLeftWidth) {
                    leftMaxWidth = curLeftWidth;
                }
            }
        }
        // Celing to 5 pixels
        const ceilingRightVal = Math.max(Math.floor(Math.ceil(rightMaxWidth / 5.0) * 5), 0);
        const ceilingLeftVal = Math.max(Math.floor(Math.ceil(leftMaxWidth / 5.0) * 5), 0);
        res.PreferredWidthScales = ceilingRightVal;
        res.PreferredWidthScalesLeft = ceilingLeftVal;
        return res;
    }
    SynchronizeBeforeDraw() {
        const barsCount = this.BarsCount();
        if (barsCount === 0) {
            return;
        }
        // Synhronize i1, XScale between all windows
        for (let i = 1; i < this.windowsContainer.Windows.length; i++) {
            this.windowsContainer.Windows[i].i1 = this.mainWindow.i1;
            this.windowsContainer.Windows[i].XScale = this.mainWindow.XScale;
        }
    }
    DrawNoData(gr) {
        const font = ThemeManager.Fonts.Font_14F_regular;
        const r = new Rectangle(0, 0, this.w, this.h);
        const gap = 16;
        const imgSize = 48;
        const textSize = gr.GetTextSize(this.NoDataMessage, font, false);
        const totalHeight = imgSize + gap + textSize.height;
        const centerX = r.Width / 2;
        const centerY = r.Height / 2;
        const imgRect = new Rectangle(centerX - imgSize / 2, centerY - totalHeight / 2, imgSize, imgSize);
        const textRect = new Rectangle(centerX - textSize.width / 2, imgRect.Y + imgSize + gap, textSize.width, textSize.height);
        if (!this.LoadingNow) {
            const img = ThemeManager.NewImages.chartIcon;
            gr.DrawCenteredImage(img.image, imgRect); /* Loading is handled by the ChartPanel's autoLoadingNowClass animation, while an icon is displayed on the chart if no data is available. */
        }
        gr.DrawStringInRect(this.NoDataMessage, font, this.TerceraChartNoDataBrushColor, textRect, false);
    }
    Draw() {
        this.SynchronizeBeforeDraw();
        this.clearOldChartImage();
        this.UpdateHasCorrectData();
        if (!this.HasCorrectData()) {
            this.DrawNoData(this.context);
            return;
        }
        // Set correct min/max for windows
        this.CalculateMinMax();
        this.DrawRenderersOnLayers();
        this.drawLayersData();
    }
    DrawRenderersOnLayers() {
        const len = this.layersCashedData.layersLength;
        // Get all drawing params
        const advParams = this.GetAdvancedParamsForRenderer(this.context);
        const w = this.windowsContainer.Windows;
        const l = w.length;
        for (let i = 0; i < l; i++) {
            if (w[i].CalcScales()) {
                this.IsDirty(true);
                this.clearLayers();
            }
        }
        for (let i = 0; i < len; i++) {
            const key = this.layersCashedData.keys[i];
            const lay = this.layers[key];
            if (!lay.isDirty) {
                continue;
            }
            const gr = lay.context;
            advParams.layerId = parseInt(key);
            // Start drawing renderers
            this.windowsContainer.Draw(gr, advParams);
        }
    }
    AddForceUpdate() {
        for (let i = 0; i < this._forceUpdateInitiators.length; i++) {
            const rend = this._forceUpdateInitiators[i];
            if (rend.IsNeedForceUpdate()) {
                this.IsDirty(rend.GetAssignLayer());
            }
        }
    }
    // #endregion
    UpdateHasCorrectData() {
        const hasCorrectData = this.HasCorrectData();
        const onHasCorrectDataChanged = this.oldHasCorrectData !== hasCorrectData;
        if (onHasCorrectDataChanged) {
            this.oldHasCorrectData = hasCorrectData;
            this.onHasCorrectDataChanged.Raise(hasCorrectData);
        }
    }
    Dispose() {
        this.windowsContainer.Dispose();
        this.clearContextAndLayers();
    }
    isNeedUpdateRightIndex(newIndex) {
        return this.rightBarIndexIncludingEmptyBars !== newIndex;
    }
    getToBeginOffset() {
        return 0;
    }
    layoutMainWindow() {
        const advParams = this.GetAdvancedParamsForRenderer(this.context);
        this.mainWindow.BeforeDrawing(advParams);
    }
    clearOldChartImage() {
        this.context.clearRect(0, 0, this.w, this.h);
        this.clearLayers();
    }
    clearContextAndLayers() {
        var _a;
        (_a = this.context) === null || _a === void 0 ? void 0 : _a.clearRect(0, 0, this.w, this.h);
        this.context = null;
        const len = this.layersCashedData.layersLength;
        for (let i = 0; i < len; i++) {
            const lay = this.layers[this.layersCashedData.keys[i]];
            lay.context.clearRect(0, 0, this.w, this.h);
            lay.canvas.width = 0;
            lay.canvas.height = 0;
            lay.canvas.remove();
            lay.canvas = null;
            lay.context = null;
        }
        this.layers = {};
        this.layersCashedData = {};
    }
    checkBarsCountAfterEvents() { }
}
